export const OPEN_STATUSES = ['pending', 'sent', 'in_progress', 'opened', 'awaiting'];

const STATUS_CLASS_RULES = [
  ['bg-success-subtle text-success-emphasis', ['complete', 'signed']],
  ['bg-danger-subtle text-danger-emphasis', ['declined', 'error', 'failed']],
  ['bg-warning-subtle text-warning-emphasis', ['progress', 'open', 'view']],
  ['bg-primary-subtle text-primary', ['sent', 'pending', 'await']]
];

export function value_or_empty(value) {
  return value || '';
}

export function normalize_text(value) {
  return String(value || '').trim();
}

export function normalize_search_text(value) {
  return normalize_text(value).toLowerCase();
}

export function get_current_role() {
  if (typeof window === 'undefined') {
    return '';
  }

  return normalize_text(window.sessionStorage.getItem('current_role')).toLowerCase();
}

function normalize_status_key(value) {
  return normalize_text(value).toLowerCase();
}

function format_status_label(value) {
  const normalized_status = normalize_status_key(value);
  if (!normalized_status) {
    return 'Pending';
  }

  return normalized_status
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function format_display_date(value) {
  const raw_value = normalize_text(value);
  if (!raw_value) {
    return '--';
  }

  const parsed_date = new Date(raw_value);
  if (Number.isNaN(parsed_date.getTime())) {
    return raw_value;
  }

  return parsed_date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function get_matching_status_class(normalized_status) {
  const matching_rule = STATUS_CLASS_RULES.find(([, matches]) => (
    matches.some((match) => normalized_status.includes(match))
  ));
  return matching_rule?.[0] || 'bg-secondary-subtle text-secondary-emphasis';
}

function get_status_class(status_label) {
  return get_matching_status_class(normalize_status_key(status_label));
}

function get_contract_meta_values(document) {
  return [normalize_text(document?.entity_profile_name), normalize_text(document?.workflow_name)].filter(Boolean);
}

function get_contract_meta(document) {
  return get_contract_meta_values(document).join(' - ') || 'Sent for signing';
}

function build_signing_document_identity(document) {
  return {
    id: normalize_text(document?.id || document?.docuseal_submission_id || document?.submission_id),
    submission_id: Number(document?.id || 0),
    docuseal_submission_id: normalize_text(document?.docuseal_submission_id)
  };
}

function get_signing_document_title(document) {
  return normalize_text(document?.file_name || document?.title) || 'Sent contract';
}

function get_signing_document_type(document) {
  return normalize_text(document?.file_type || document?.type) || 'Contract';
}

function get_signing_document_sent_on(document) {
  return format_display_date(document?.sent_at || document?.created_at);
}

function build_signing_document_content(document) {
  return {
    title: get_signing_document_title(document),
    meta: get_contract_meta(document),
    type: get_signing_document_type(document),
    sent_on: get_signing_document_sent_on(document)
  };
}

function build_signing_document_status(document) {
  const raw_status = normalize_status_key(document?.status || document?.overall_status);
  return {
    status: format_status_label(raw_status),
    raw_status,
    status_class: get_status_class(raw_status)
  };
}

export function build_signing_document(document) {
  const signing_document_context  = {
    ...document,
    ...build_signing_document_identity(document),
    ...build_signing_document_content(document),
    ...build_signing_document_status(document),
    can_track: Boolean(document?.can_track || document?.docuseal_submission_id)
  };
  return signing_document_context;
}

export function matches_document_filter(document, filter_id) {
  if (filter_id === 'open') {
    return OPEN_STATUSES.includes(document.raw_status);
  }

  if (filter_id === 'completed') {
    return document.raw_status === 'completed';
  }

  if (filter_id === 'declined') {
    return document.raw_status === 'declined';
  }

  return true;
}

export function matches_document_search(document, search_term) {
  const normalized_search = normalize_text(search_term).toLowerCase();
  if (!normalized_search) {
    return true;
  }

  const searchable_text = [
    document?.title,
    document?.meta,
    document?.type,
    document?.sent_on,
    document?.status
  ]
    .map((value) => normalize_text(value).toLowerCase())
    .join(' ');

  return searchable_text.includes(normalized_search);
}

export function build_signing_filter_options(signing_documents = []) {
  return [
    { id: 'all', label: 'All' },
    { id: 'open', label: 'Open' },
    { id: 'completed', label: 'Completed' },
    { id: 'declined', label: 'Declined' }
  ].map((filter) => ({
    ...filter,
    count: signing_documents.filter((document) => matches_document_filter(document, filter.id)).length
  }));
}

export function build_selected_case_subtitle(selected_case = {}) {
  return [
    normalize_text(selected_case?.journey),
    normalize_text(selected_case?.detail),
    normalize_text(selected_case?.company_name)
  ].filter(Boolean).join(' · ');
}

export function filter_gate_cases(gate_cases = [], search_term = '') {
  const normalized_search = normalize_search_text(search_term);
  if (!normalized_search) {
    return Array.isArray(gate_cases) ? gate_cases : [];
  }

  return (Array.isArray(gate_cases) ? gate_cases : []).filter((case_item) => {
    const searchable_text = [
      case_item?.name,
      case_item?.journey,
      case_item?.workflow_display_stage,
      case_item?.current_workflow_status,
      case_item?.detail,
      case_item?.company_name,
      case_item?.owner,
      case_item?.onboarding_owner_role_name
    ]
      .map((value) => normalize_search_text(value))
      .join(' ');

    return searchable_text.includes(normalized_search);
  });
}
